// mapEditorConstants.js — module-level constants for MapEditorModal (ticket 01 extraction).

import { OVERLAY_TYPES } from '../../schema/mapSchema';

// -- Ticket 02 (map-editor-modal-ux-audit): named z-index scale for the map-canvas overlay
// stack ------------------------------------------------------------------------------------
// Canonical stacking order (top-to-bottom, i.e. highest zIndex first) for every absolutely-
// positioned overlay stacked on top of the map canvas Box (the one wrapping `mapContainerRef`).
// A future addition should pick its place in this list instead of a hand-picked number.
//
//   MAP_OVERLAY_Z.LOADING           (10) — full-canvas "Loading map…" spinner overlay, shown
//                                          until `mapReady`.
//   MAP_OVERLAY_Z.DRAG_OVER         (9)  — ticket 05 drag-over highlight (tint + dashed border)
//                                          while a symbol drag from the sidebar is over the canvas.
//   MAP_OVERLAY_Z.PLACEMENT_WIZARD  (8)  — bottom-center "click to place" HUD (PlacementWizard).
//   MAP_OVERLAY_Z.GRID_CANVAS       (4)  — coord-grid label `<canvas>` (drawCoordGridLabels).
//   MAP_OVERLAY_Z.CONTROLS          (3)  — shared tier: the ticket-01 floating control column
//                                          (`Stack` wrapping MovementTrailControl/BasemapControl/
//                                          CoordGridControl — the individual `FloatingControlShell`
//                                          instances inside it have no z-index of their own, they're
//                                          plain flow children), the "Drawing on: <layer>" Chip, the
//                                          DTG `MapTimeline` (../MapTimeline.jsx), and the canvas-
//                                          variant `MapDrawToolbar` (../../mapEditor/MapDrawToolbar.jsx)
//                                          all share this value. The latter two set `zIndex: 3`
//                                          locally in their own files (not imported from here) —
//                                          listed here anyway so this stays the single reference
//                                          for the *full* stacking order, per this ticket.
//   MAP_OVERLAY_Z.VIGNETTE          (1)  — 16:9 output-frame overlay (VignetteOverlay); its inner
//                                          frame box and the "16:9 output frame" label box below it
//                                          have no explicit zIndex — they stack via normal DOM
//                                          order within this same stacking context.
//
// Not part of this scale: `applyImageryCoverage`'s OL `TileLayer` has its own `zIndex: 1` prop
// (see the comment above that function) — that's an OpenLayers *map layer* zIndex controlling
// tile/vector paint order inside the OL renderer, an entirely separate stacking system from the
// DOM `zIndex` CSS values above despite the shared property name. Left as a bare literal.
export const MAP_OVERLAY_Z = Object.freeze({
    VIGNETTE: 1,
    CONTROLS: 3,
    GRID_CANVAS: 4,
    PLACEMENT_WIZARD: 8,
    DRAG_OVER: 9,
    LOADING: 10,
});

// Ticket 01: top row toggles the map's "Overlay" between the existing 4-style vector picker
// ("Mapbox Style") and the NASA GIBS satellite raster ("Satellite") — see `applyImageryCoverage`
// for how the latter is actually rendered. The 4 style buttons below only make sense while
// overlayType is 'mapbox' (satellite ignores `basemapStyleId` entirely), so they're hidden
// while 'satellite' is active rather than left visible-but-inert.
export const OVERLAY_TYPE_OPTIONS = [
    {id: OVERLAY_TYPES.MAPBOX, label: 'Mapbox'},
    {id: OVERLAY_TYPES.SATELLITE, label: 'Satellite'},
];

// Ticket 01 (map-editor-modal-ux-audit): shared style for a collapsible floating control's
// option row — a real <button> (via ButtonBase, for native keyboard/focus/click semantics)
// rather than a plain onClick Box, sized for a legible/tappable footprint (was 9px/very
// cramped) now that rows don't need to stay tiny to avoid overlapping their neighbors — see
// FloatingControlShell below for why overlap is no longer a hand-tuned-offset concern.
export const controlOptionRowSx = (theme, selected) => ({
    width: '100%', px: 1, py: 0.85, fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
    justifyContent: 'center', textAlign: 'center', textTransform: 'uppercase',
    color: selected ? 'primary.main' : 'text.disabled',
    bgcolor: selected ? 'primary.50' : 'transparent',
    borderTop: `1px solid ${theme.palette.divider}`,
    '&:hover': { bgcolor: 'action.hover' }, transition: 'background-color 0.1s',
});

// rendering-hoist-jsx: static array hoisted outside component to avoid re-creation on every render
export const COORD_GRID_OPTIONS = ['MGRS', 'UTM', 'WGS84'];
